import { Box, Chip, chipClasses, Stack, styled } from '@mui/material';
import { useTranslation } from 'src/hooks/use-translation';

import { useResponsive } from 'src/hooks/use-responsive';

import { Platform } from 'src/types';
import { Image } from 'src/components/image';

import StudentFaviconOutlineSvg from 'icons/colored/student-favicon-outline.svg';

const StyledChip = styled(Chip)(({ theme }) => ({
  marginLeft: 'auto',
  borderRadius: '8px',
  textTransform: 'capitalize',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  padding: 0,
  height: 28,
  [`.${chipClasses.label}`]: {
    paddingInline: 7,
  },
}));

export const PlatformItem = ({
  adaptive,
  name,
  role,
  icon,
}: Pick<Platform, 'role' | 'name'> & {
  adaptive?: boolean;
  icon: string | undefined;
}) => {
  const { t, i18n } = useTranslation('', { keyPrefix: 'platform-popover.roles' });
  const smUp = useResponsive('up', 'sm');

  const roleLabel = i18n.exists(`platform-popover.roles.${role}`) ? t(role) : role;

  const showDetails = smUp || !adaptive;

  const iconA11yProps = (() => {
    if (icon) return { alt: showDetails ? '' : name };
    return showDetails ? { 'aria-hidden': true } : { role: 'img', 'aria-label': name };
  })();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ maxWidth: '280px', width: '100%' }}
    >
      <Box
        component={icon ? Image : StudentFaviconOutlineSvg}
        width={24}
        height={24}
        src={icon}
        {...iconA11yProps}
        sx={{
          flexShrink: 0,
          color: (theme) => theme.palette.text.secondary,
          mr: '0!important',
          display: 'flex',
        }}
      />

      {showDetails && (
        <Box
          sx={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </Box>
      )}

      {showDetails && <StyledChip label={roleLabel} variant="soft" clickable={false} />}
    </Stack>
  );
};
